import classNames from "classnames";
import { connect } from "react-redux";
import { useEffect, useRef, useState } from "react";

import {
  getIsCardslistLoading,
  getNavigationClusterLoader,
} from "redux/SingleDataset/selectors";
import ScrollAwareHover from "views/components/ScrollAwareHover";
import useHoverDelay, { calculateAspectRatio } from "helpers/utility/utilities";

import styles from "./style.module.scss";
import { RelevanceType } from "types";
import RelevanceTypeIcon from "views/uikit/RelevanceTypeIcon";
import ImageWithFallback from "views/components/ImageWithFallback";
import { FrameIndication } from "../FrameIndication/FrameIndication";
import { isNullOrUndefined } from "../../../../helpers/utility/isNullOrUndefined";

type Dimensions = { height: number; width: number };

interface BaseImageBoxProps {
  image: string;
  hoverDelay?: number;
  showVideoIcon?: boolean;
  showAssetPreview?: boolean;
  isCardslistLoading?: boolean;
  relevanceType?: RelevanceType | null;
  isNavigationClusterLoading?: boolean;
  onUpdateAssetData?: () => void;
  onEndContentLoading?: () => void;
  inReview?: boolean;
  timestamp?: number;
}

// Simplified props for SimpleImageBox
interface SimpleImageBoxProps extends BaseImageBoxProps {
  imageThumb?: string;
}

// Props for ObjectImageBox, with required boundingBox
interface ObjectImageBoxProps extends BaseImageBoxProps {
  hoverDelay: number;
  boundingBox: number[];
}

// Props for the main ImageBox component
export interface ImageBoxProps extends BaseImageBoxProps {
  isObject?: boolean;
  imageThumb?: string;
  boundingBox?: number[] | null;
}

export const SimpleImageBox = ({
  image,
  imageThumb,
  showVideoIcon,
  showAssetPreview,
  isCardslistLoading,
  isNavigationClusterLoading,
  //optional
  hoverDelay = 0,
  relevanceType = null,
  onUpdateAssetData = () => {},
  onEndContentLoading: endContentLoading = () => {},
  inReview,
  timestamp,
}: SimpleImageBoxProps) => {
  const [loading, setLoading] = useState(true);
  const showRelevance = relevanceType && !loading;

  const imageContainerClassnames = classNames(styles.squareImagesContainer, {
    [styles.squareImagesContainerForAssetPreview]: showAssetPreview,
  });

  const { handleMouseEnter, handleMouseLeave } = useHoverDelay(
    onUpdateAssetData,
    hoverDelay,
  );

  const handleImageLoad = () => {
    setLoading(false);
    if (isNavigationClusterLoading || isCardslistLoading) {
      endContentLoading();
    }
  };

  const handleImageError = () => {
    setLoading(false);
    if (isNavigationClusterLoading || isCardslistLoading) {
      endContentLoading();
    }
  };

  return (
    <ScrollAwareHover
      className={imageContainerClassnames}
      data-testid="single-image-container"
      onMouseOver={!inReview ? handleMouseEnter : () => {}}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.tableColumn}>
        {!isNullOrUndefined(timestamp) && (
          <FrameIndication timestamp={timestamp!} />
        )}
        {showRelevance && <RelevanceTypeIcon type={relevanceType} />}
        <ImageWithFallback
          thumbImage={{ src: imageThumb || "" }}
          plainImage={{ src: image }}
          className={styles.singleImage}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </div>
    </ScrollAwareHover>
  );
};

const ObjectImageBox = ({
  image,
  hoverDelay,
  boundingBox,
  relevanceType,
  showVideoIcon,
  showAssetPreview,
  isCardslistLoading,
  onEndContentLoading,
  onUpdateAssetData = () => {},
  isNavigationClusterLoading,
  timestamp,
}: ObjectImageBoxProps) => {
  const defaultDimensions: Dimensions = { height: 0, width: 0 };
  const [loading, setLoading] = useState(true);
  const [imageDmns, setImageDmns] = useState(defaultDimensions);
  const [bboxContainerDmns, setBboxContainerDmns] = useState(defaultDimensions);

  const objectImageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const showRelevance = relevanceType && !loading;

  const objectImageWrapperClassnames = classNames(styles.objectImageWrapper, {
    [styles.objectImageWrapperForAssetPreview]: showAssetPreview,
  });

  const calculateNewImageDimensions = () => {
    if (objectImageRef.current) {
      let newValues = calculateAspectRatio(objectImageRef.current);
      setBboxContainerDmns({ height: newValues[0], width: newValues[1] });
    }
  };

  const handleLoad = () => {
    setLoading(false);
    calculateNewImageDimensions();
    setImageDmns({
      height: objectImageRef.current?.naturalHeight ?? 0,
      width: objectImageRef.current?.naturalWidth ?? 0,
    });
    if (isNavigationClusterLoading || isCardslistLoading) {
      if (onEndContentLoading) onEndContentLoading();
    }
  };

  const boundingBoxContainerStyle = () => {
    if (bboxContainerDmns.height > 0 && bboxContainerDmns.width > 0) {
      return {
        height: `${bboxContainerDmns.height}px`,
        width: `${bboxContainerDmns.width}px`,
      };
    }
    return undefined;
  };

  const boundingBoxStyle = () => {
    let heightRatio = 100;
    let widthRatio = 100;

    if (imageDmns.height > 0 && imageDmns.width > 0) {
      heightRatio = (bboxContainerDmns.height / imageDmns.height) * 100;
      widthRatio = (bboxContainerDmns.width / imageDmns.width) * 100;
    }

    return {
      left: (boundingBox[0] * widthRatio) / 100,
      top: (boundingBox[1] * heightRatio) / 100,
      width: (boundingBox[2] * widthRatio) / 100,
      height: (boundingBox[3] * heightRatio) / 100,
      display: loading ? "none" : "block",
    };
  };

  const { handleMouseEnter, handleMouseLeave } = useHoverDelay(
    onUpdateAssetData,
    hoverDelay,
  );

  useEffect(() => {
    calculateNewImageDimensions();
    const objectImageElement = objectImageRef.current;
    if (!objectImageElement) return;

    const resizeObserver = new ResizeObserver(calculateNewImageDimensions);
    resizeObserver.observe(objectImageElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [image]);

  return (
    <ScrollAwareHover
      className={objectImageWrapperClassnames}
      ref={containerRef}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <>
        <div
          className={styles.boundingBoxContainer}
          style={boundingBoxContainerStyle()}
        >
          {boundingBox && (
            <div
              className={styles.boundingBox}
              style={boundingBoxStyle()}
              data-testid="bounding-box"
            ></div>
          )}
        </div>
        {!isNullOrUndefined(timestamp) && (
          <FrameIndication timestamp={timestamp!} />
        )}
        {showRelevance && <RelevanceTypeIcon type={relevanceType} />}
        <img
          ref={objectImageRef}
          className={styles.objectImage}
          data-testid="object-image"
          src={image}
          alt="object img"
          loading="lazy"
          onLoad={handleLoad}
          onError={(e) => {
            console.error(`Error loading object image: ${image}`, e);
            setLoading(false);
            if (isNavigationClusterLoading || isCardslistLoading) {
              if (onEndContentLoading) onEndContentLoading();
            }
          }}
        />
      </>
    </ScrollAwareHover>
  );
};

const ImageBox = ({
  image,
  imageThumb,
  isCardslistLoading,
  isNavigationClusterLoading,
  hoverDelay = 0,
  isObject = false,
  boundingBox = [],
  relevanceType = null,
  showVideoIcon = false,
  showAssetPreview = true,
  onUpdateAssetData = () => {},
  onEndContentLoading = () => {},
  inReview,
  timestamp,
}: ImageBoxProps) => {
  return isObject && !!boundingBox?.length ? (
    <ObjectImageBox
      image={image}
      hoverDelay={hoverDelay}
      boundingBox={boundingBox}
      showVideoIcon={showVideoIcon}
      relevanceType={relevanceType}
      showAssetPreview={showAssetPreview}
      isCardslistLoading={isCardslistLoading}
      isNavigationClusterLoading={isNavigationClusterLoading}
      onUpdateAssetData={onUpdateAssetData}
      onEndContentLoading={onEndContentLoading}
      timestamp={timestamp}
    />
  ) : (
    <SimpleImageBox
      image={image}
      imageThumb={imageThumb}
      hoverDelay={hoverDelay}
      showVideoIcon={showVideoIcon}
      relevanceType={relevanceType}
      showAssetPreview={showAssetPreview}
      isCardslistLoading={isCardslistLoading}
      isNavigationClusterLoading={isNavigationClusterLoading}
      onUpdateAssetData={onUpdateAssetData}
      onEndContentLoading={onEndContentLoading}
      inReview={inReview}
      timestamp={timestamp}
    />
  );
};

const mapStateToProps = (state: any) => {
  return {
    isCardslistLoading: getIsCardslistLoading(state),
    isNavigationClusterLoading: getNavigationClusterLoader(state),
  };
};

export default connect(mapStateToProps)(ImageBox);
